import React from "react";
import { useTheme } from "react-jss";
import classnames from "classnames";
import { RendererProps } from "../BaseRenderer";
import { getSettingString, hasSetting } from "../../../models/utilities/settingUtilities";
import { AxisModel } from "../../../models/AxisModel";
import { ValidationResultModel } from "../../../models/ValidationResultModel";
import { cellHasError } from "../../../models/utilities/gridUtilities";
import { ChoiceModel } from "../../../models/ChoiceModel";
import useStyle from "./GridCellRenderer.styles";
import { QuestionType } from "../../../models/enums/QuestionType";

interface Props extends RendererProps {
    isColumnHeader?: boolean;
    isChoiceHeader?: boolean;
    isRowHeader?: boolean;
    row?: AxisModel;
    column?: AxisModel;
    colSpan?: number;
    validationResult?: ValidationResultModel;
    choice?: ChoiceModel;
    columnIndex?: number;
}

enum HeaderType {
    Choice,
    Column,
    Row,
}

const headerProps = {
    [HeaderType.Choice]: {
        role: "columnheader",
        scope: "col",
    },
    [HeaderType.Column]: {
        role: "columnheader",
        scope: "col",
    },
    [HeaderType.Row]: {
        role: "rowheader",
        scope: "row",
    },
};

export default function GridCellRenderer(props: Props) {
    const {
        row,
        column,
        validationResult,
        isChoiceHeader,
        isColumnHeader,
        isRowHeader,
        colSpan,
        children,
        choice,
        question,
    } = props;
    const theme = useTheme();
    const classes = useStyle({ theme });
    const inputSize = getSettingString(column, "Size");

    const errorClass = cellHasError(row, column, validationResult) ? classes.gridItemError : "";
    const rowColor = hasSetting(row, "Color") ? getSettingString(row, "Color") : null;
    const columnColor = hasSetting(column, "Color") ? getSettingString(column, "Color") : null;
    const choiceColor = hasSetting(choice, "Color") ? getSettingString(choice, "Color") : null;
    const isStarRatting = question?.type === QuestionType.StarRatingGrid;

    const style = {
        backgroundColor: rowColor || columnColor || choiceColor,
        width: inputSize === "Full" ? "100%" : undefined,
        display: isRowHeader && isStarRatting ? "table-cell" : null,
    };

    let headerClass: string;
    let headerType: HeaderType;
    const attributes = {};

    if (isChoiceHeader) {
        headerType = HeaderType.Choice;
        headerClass = classes.gridChoiceHeader;
        if (choice && choice.index >= 0) {
            const questionName = question?.name ?? "";
            const columnIndex = column?.index ?? 0; // Hybrid choice will have 2 level of header

            if (question.type === QuestionType.HybridGrid) {
                attributes["id"] = `Q_${questionName}_MAINCOLHEADER${
                    columnIndex + 1
                }_CHOICEHEADER_${choice.index + 1}`;
            } else {
                attributes["id"] = `Q_${questionName}_CHOICEHEADER_${choice.index + 1}`;
            }
        }
    } else if (isColumnHeader) {
        headerType = HeaderType.Column;
        headerClass = classes.gridColumnHeader;
    } else if (isRowHeader) {
        headerType = HeaderType.Row;
        headerClass = classes.gridRowHeader;
    } else {
        return (
            <td
                className={classnames(classes.gridItem, errorClass)}
                colSpan={colSpan}
                style={style}
                role="cell"
            >
                {children}
            </td>
        );
    }

    const { role, scope } = headerProps[headerType];
    return (
        <th
            className={classnames(classes.gridItem, headerClass, errorClass)}
            colSpan={colSpan}
            role={role}
            scope={scope}
            style={style}
            {...attributes}
        >
            {children}
        </th>
    );
}
